// @ts-check

import React from "react";
import { Button } from "antd";

import { useImportAccountData } from "../../../data/use-accounts";
import { useImportContactData } from "../../../data/use-contacts";
import { useImportOpportunityData } from "../../../data/use-opportunities";
import { useImportVendorData } from "../../../data/use-vendors";
import { useImportActivityData } from "../../../data/use-activities";
import { RowInfos } from "./row-infos";

/** @typedef {import("@evolved/domain").UserDefinedField[]} UDFs */

/**
 * @param {Object} o
 * @param {UDFs} o.existingUdfs
 * @param {UDFs} o.newUdfs
 * @param {Record<string, {id: string; label: string;}[]>} o.selectOptionsUpdate
 */
const prepareUdfPayload = ({ existingUdfs, newUdfs, selectOptionsUpdate }) => {
  let prepared = [...existingUdfs];

  for (const udf of newUdfs) {
    if (prepared.find(({ id }) => udf.id === id)) {
      continue;
    }

    prepared.push(udf);
  }

  prepared = prepared.map((udf) => {
    if (udf.dataType === "SELECT" && selectOptionsUpdate[udf.id]) {
      return {
        ...udf,
        options: [...udf.options, ...(selectOptionsUpdate[udf.id] ?? [])],
      };
    }

    return udf;
  });

  return prepared;
};

/**
 * @param {Object} props
 * @param {import("./domain").ImportableEntityTypes} props.entityType
 * @param {UDFs} props.existingUdfs
 * @param {UDFs} props.newUdfs
 * @param {import("./domain").ImportState} props.state
 * @param {() => void} props.onBack
 * @param {() => void} props.onSuccess
 */
export const Confirm = ({
  entityType,
  newUdfs,
  existingUdfs,
  state,
  onBack,
  onSuccess,
}) => {
  const importData = {
    ACCOUNT: useImportAccountData({
      onSuccess,
    }),
    ACTIVITY: useImportActivityData({
      onSuccess,
    }),
    CONTACT: useImportContactData({
      onSuccess,
    }),
    OPPORTUNITY: useImportOpportunityData({
      onSuccess,
    }),
    VENDOR: useImportVendorData({
      onSuccess,
    }),
  }[entityType];

  const { reducedRows, selectOptionsUpdate } = state;

  const onConfirm = () => {
    importData.mutate({
      entities: reducedRows
        .filter(({ isValid }) => {
          return isValid;
        })
        .map(({ entity }) => entity),
      userDefinedFields: prepareUdfPayload({
        newUdfs,
        existingUdfs,
        selectOptionsUpdate,
      }),
    });
  };

  const updateCount = reducedRows.filter(
    ({ isValid, previous }) => isValid && !!previous
  ).length;
  const newCount = reducedRows.filter(
    ({ isValid, previous }) => isValid && !previous
  ).length;
  const warningCount = reducedRows.filter(
    ({ info, isValid }) =>
      isValid && info.some(({ level }) => level === "warning")
  ).length;
  const issueCount = reducedRows.filter(({ isValid }) => !isValid).length;

  return (
    <div>
      <div className="mb-2">
        You are about to update {updateCount} and create {newCount} records
        where:
      </div>
      <div className="mb-4">
        {warningCount} have warnings that will be imported and {issueCount} have
        errors that will not be imported.
      </div>

      <RowInfos
        reducedRows={reducedRows}
        filter={(info) => {
          if (info.level === "info") {
            return false;
          }

          return true;
        }}
      />

      <div className="my-4">Would you like to proceed?</div>

      <div className="flex justify-end">
        <Button
          className="mr-2"
          disabled={importData.isLoading}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          type="primary"
          onClick={onConfirm}
          loading={importData.isLoading}
          disabled={importData.isLoading || (!newCount && !updateCount)}
        >
          Confirm Import
        </Button>
      </div>
    </div>
  );
};
